// src/utils/uploadQueueManager.ts

interface UploadQueueItem {
  id: string;
  cardId: number;
  type: 'screenshot' | 'recording';
  feedbackId: string;
  timestamp: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  progress: number;
  error?: string;
  retryCount: number;
  hasAudio?: boolean;
  hasThumbnail?: boolean;
  hasAnnotation?: boolean;
}

class UploadQueueManager {
  private static STORAGE_KEY = 'upload_queue';
  private static MAX_RETRY_COUNT = 3;
  private static listeners: Map<string, (item: UploadQueueItem) => void> = new Map();
  private static globalListeners: Set<(queue: UploadQueueItem[]) => void> = new Set();

  static getQueue(): UploadQueueItem[] {
    try {
      const queue = localStorage.getItem(this.STORAGE_KEY);
      return queue ? JSON.parse(queue) : [];
    } catch (error) {
      console.error('❌ Error reading upload queue:', error);
      return [];
    }
  }

  private static saveQueue(queue: UploadQueueItem[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(queue));
      
      // ✅ Dispatch multiple events for different components
      window.dispatchEvent(new CustomEvent('upload-queue-updated', { 
        detail: { queue } 
      }));

      // ✅ Specific event for when uploads complete
      const completed = queue.filter(i => i.status === 'completed');
      if (completed.length > 0) {
        window.dispatchEvent(new CustomEvent('uploads-completed', {
          detail: { completed }
        }));
      }

      this.globalListeners.forEach(listener => {
        try {
          listener(queue);
        } catch (error) {
          console.error('❌ Error in global listener:', error);
        }
      });

      console.log('💾 Upload queue saved:', {
        totalItems: queue.length,
        pending: queue.filter(i => i.status === 'pending').length,
        uploading: queue.filter(i => i.status === 'uploading').length,
        completed: queue.filter(i => i.status === 'completed').length,
        failed: queue.filter(i => i.status === 'failed').length,
      });
    } catch (error) {
      console.error('❌ Error saving upload queue:', error);
    }
  }

  static addToQueue(item: Omit<UploadQueueItem, 'status' | 'progress' | 'retryCount'>): void {
    const queue = this.getQueue();
    
    const existingIndex = queue.findIndex(q => q.id === item.id);
    if (existingIndex !== -1) {
      console.warn('⚠️ Item already in queue, updating:', item.id);
      queue[existingIndex] = {
        ...queue[existingIndex],
        ...item,
        status: 'pending',
        progress: 0,
      };
    } else {
      const newItem: UploadQueueItem = {
        ...item,
        status: 'pending',
        progress: 0,
        retryCount: 0,
      };
      queue.push(newItem);
    }
    
    this.saveQueue(queue);
    console.log('✅ Added to upload queue:', {
      id: item.id,
      type: item.type,
      cardId: item.cardId,
    });
  }

  // ✅ FIXED: Ensure progress reaches 100% before showing next item
  static updateItem(id: string, updates: Partial<UploadQueueItem>): void {
    const queue = this.getQueue();
    const index = queue.findIndex(item => item.id === id);
    
    if (index !== -1) {
      const oldStatus = queue[index].status;
      const oldProgress = queue[index].progress;
      
      // ✅ CRITICAL FIX: Always ensure progress reaches 100% before completion
      if (updates.status === 'completed') {
        console.log('🎯 Upload completing at', oldProgress + '%, ensuring 100% first...');
        
        // Step 1: ALWAYS force progress to 100% first, regardless of current progress
        queue[index] = {
          ...queue[index],
          progress: 100,
          status: 'uploading' // Keep as uploading to show the 100% progress
        };
        this.saveQueue(queue);
        
        console.log('📊 Progress set to 100%, showing upload completion animation...');
        
        // Step 2: Wait for progress bar animation (1 second for user to see 100%)
        setTimeout(() => {
          const currentQueue = this.getQueue();
          const currentIndex = currentQueue.findIndex(item => item.id === id);
          
          if (currentIndex !== -1) {
            currentQueue[currentIndex] = {
              ...currentQueue[currentIndex],
              status: 'completed',
              progress: 100
            };
            this.saveQueue(currentQueue);
            
            console.log('✅ Status changed to "completed" after showing 100%');
            
            // Step 3: Keep showing completed checkmark for 1.5 seconds
            setTimeout(() => {
              const item = currentQueue[currentIndex];
              
              window.dispatchEvent(new CustomEvent('upload-item-completed', {
                detail: { 
                  item: item,
                  cardId: item.cardId,
                  type: item.type,
                  feedbackId: item.feedbackId
                }
              }));
              
              window.dispatchEvent(new CustomEvent('refresh-card-data', {
                detail: { 
                  cardId: item.cardId,
                  immediate: true
                }
              }));
              
              console.log('🔄 Refresh events dispatched - new feedback will now appear');
            }, 1500); // Show completed status for 1.5 seconds
          }
        }, 1000); // Wait 1 second for progress bar to animate to 100%
        
        return; // Exit early to prevent immediate status change
      }
      
      // Normal update for all other cases (progress updates during upload)
      queue[index] = { ...queue[index], ...updates };
      this.saveQueue(queue);
      
      const listener = this.listeners.get(id);
      if (listener) {
        try {
          listener(queue[index]);
        } catch (error) {
          console.error('❌ Error in item listener:', error);
        }
      }

      console.log('🔄 Updated queue item:', {
        id,
        status: queue[index].status,
        progress: queue[index].progress,
      });
    } else {
      console.warn('⚠️ Item not found in queue:', id);
    }
  }

  static removeItem(id: string): void {
    const queue = this.getQueue();
    const item = queue.find(i => i.id === id);
    const filtered = queue.filter(item => item.id !== id);
    this.saveQueue(filtered);
    this.listeners.delete(id);
    
    if (item) {
      console.log('✅ Removed from upload queue:', {
        id,
        type: item.type,
        status: item.status,
      });
    }
  }

  static getPendingForCard(cardId: number): UploadQueueItem[] {
    return this.getQueue().filter(
      item => item.cardId === cardId && (item.status === 'pending' || item.status === 'uploading')
    );
  }

  static getCompletedForCard(cardId: number): UploadQueueItem[] {
    return this.getQueue().filter(
      item => item.cardId === cardId && item.status === 'completed'
    );
  }

  static getFailedForCard(cardId: number): UploadQueueItem[] {
    return this.getQueue().filter(
      item => item.cardId === cardId && item.status === 'failed'
    );
  }

  static getItem(id: string): UploadQueueItem | null {
    const queue = this.getQueue();
    return queue.find(item => item.id === id) || null;
  }

  static hasItem(id: string): boolean {
    return this.getQueue().some(item => item.id === id);
  }

  static subscribe(id: string, callback: (item: UploadQueueItem) => void): () => void {
    this.listeners.set(id, callback);
    return () => this.listeners.delete(id);
  }

  static subscribeToQueue(callback: (queue: UploadQueueItem[]) => void): () => void {
    this.globalListeners.add(callback);
    return () => this.globalListeners.delete(callback);
  }

  static retryUpload(id: string): boolean {
    const item = this.getItem(id);
    if (!item) {
      console.warn('⚠️ Cannot retry: item not found', id);
      return false;
    }

    if (item.retryCount >= this.MAX_RETRY_COUNT) {
      console.warn('⚠️ Max retry count reached for:', id);
      return false;
    }

    this.updateItem(id, {
      status: 'pending',
      retryCount: item.retryCount + 1,
      error: undefined,
    });

    console.log('🔄 Retrying upload:', {
      id,
      attempt: item.retryCount + 1,
      maxRetries: this.MAX_RETRY_COUNT,
    });

    return true;
  }

  static cleanupOldItems(): void {
    const queue = this.getQueue();
    const oneHourAgo = Date.now() - 60 * 60 * 1000;
    
    const filtered = queue.filter(item => {
      const shouldKeep = !(
        (item.status === 'completed' || item.status === 'failed') && 
        item.timestamp < oneHourAgo
      );
      
      if (!shouldKeep) {
        console.log('🧹 Cleaning up old item:', {
          id: item.id,
          status: item.status,
          age: Math.floor((Date.now() - item.timestamp) / 1000 / 60) + ' minutes',
        });
      }
      
      return shouldKeep;
    });
    
    if (filtered.length !== queue.length) {
      this.saveQueue(filtered);
      console.log('✅ Cleanup complete:', {
        removed: queue.length - filtered.length,
        remaining: filtered.length,
      });
    }
  }

  static clearForCard(cardId: number): void {
    const queue = this.getQueue();
    const filtered = queue.filter(item => item.cardId !== cardId);
    const removedCount = queue.length - filtered.length;
    
    this.saveQueue(filtered);
    console.log('🗑️ Cleared all items for card:', {
      cardId,
      removed: removedCount,
    });
  }

  static clearAll(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.listeners.clear();
    this.globalListeners.clear();
    console.log('🗑️ Cleared entire upload queue');
  }

  static getStats() {
    const queue = this.getQueue();
    return {
      total: queue.length,
      pending: queue.filter(i => i.status === 'pending').length,
      uploading: queue.filter(i => i.status === 'uploading').length,
      completed: queue.filter(i => i.status === 'completed').length,
      failed: queue.filter(i => i.status === 'failed').length,
      byType: {
        screenshot: queue.filter(i => i.type === 'screenshot').length,
        recording: queue.filter(i => i.type === 'recording').length,
      },
    };
  }

  static hasActiveUploads(cardId: number): boolean {
    return this.getPendingForCard(cardId).length > 0;
  }

  static getAllForCard(cardId: number): UploadQueueItem[] {
    return this.getQueue().filter(item => item.cardId === cardId);
  }
}

export default UploadQueueManager;
export type { UploadQueueItem };